/**
 * App Bridge Provider
 *
 * Uses the new Shopify App Bridge CDN (app-bridge.js) which provides
 * the global `shopify` variable. This is required for all Shopify App
 * Store apps as of March 2024.
 *
 * In development mode (not embedded), uses mock authentication.
 */

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';

// Type declaration for the global shopify object from CDN
declare global {
  interface Window {
    shopify?: {
      config: {
        apiKey: string;
        shop: string;
        locale: string;
      };
      idToken: () => Promise<string>;
      toast: {
        show: (message: string, options?: { duration?: number; isError?: boolean }) => void;
      };
      loading: (isLoading: boolean) => void;
      modal: {
        open: (options: unknown) => void;
        close: () => void;
      };
    };
  }
}

interface AppBridgeContextValue {
  /**
   * Get a valid session token for API calls
   * Returns the token or throws an error
   */
  getSessionToken: () => Promise<string>;
  /**
   * Whether we're running inside Shopify Admin
   */
  isEmbedded: boolean;
  /**
   * The shop domain
   */
  shopDomain: string | null;
  /**
   * Show a toast notification
   */
  showToast: (message: string, isError?: boolean) => void;
  /**
   * Set loading state
   */
  setLoading: (isLoading: boolean) => void;
}

const AppBridgeContext = createContext<AppBridgeContextValue | null>(null);

/**
 * Check if we're running in development mode
 */
function isDevelopment() {
  return import.meta.env.DEV;
}

/**
 * Check if App Bridge is available (running embedded in Shopify)
 */
function isAppBridgeAvailable() {
  return typeof window !== 'undefined' && window.shopify !== undefined;
}

interface AppBridgeProviderProps {
  children: ReactNode;
}

export function AppBridgeProvider({ children }: AppBridgeProviderProps) {
  const isEmbedded = isAppBridgeAvailable();
  const isDevMode = isDevelopment() && !isEmbedded;

  const shopDomain = useMemo(() => {
    if (isEmbedded && window.shopify) {
      return window.shopify.config.shop;
    }
    // Fallback to URL param for dev mode
    const params = new URLSearchParams(window.location.search);
    return params.get('shop');
  }, [isEmbedded]);

  const getSessionToken = useCallback(async (): Promise<string> => {
    // Development mode: use mock token
    if (isDevMode) {
      console.log('[AppBridge] Development mode - using mock token');
      return 'dev-mock-token';
    }

    // Production: get token from App Bridge global
    if (!window.shopify) {
      throw new Error('Shopify App Bridge not available - not embedded in Shopify Admin');
    }

    try {
      const token = await window.shopify.idToken();
      return token;
    } catch (error) {
      console.error('[AppBridge] Failed to get session token:', error);
      throw new Error('Failed to authenticate with Shopify');
    }
  }, [isDevMode]);

  const showToast = useCallback((message: string, isError = false) => {
    if (window.shopify?.toast) {
      window.shopify.toast.show(message, { isError });
    } else {
      // Fallback for dev mode
      console.log(`[Toast] ${isError ? 'Error: ' : ''}${message}`);
    }
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    if (window.shopify?.loading) {
      window.shopify.loading(isLoading);
    }
  }, []);

  const value = useMemo(
    () => ({
      getSessionToken,
      isEmbedded,
      shopDomain,
      showToast,
      setLoading,
    }),
    [getSessionToken, isEmbedded, shopDomain, showToast, setLoading]
  );

  return (
    <AppBridgeContext.Provider value={value}>
      {children}
    </AppBridgeContext.Provider>
  );
}

/**
 * Hook to access App Bridge context
 */
export function useAppBridge() {
  const context = useContext(AppBridgeContext);
  if (!context) {
    throw new Error('useAppBridge must be used within AppBridgeProvider');
  }
  return context;
}
